import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  INITIAL_CREATE_MISSION_DATA,
  confirmSlideOverClose,
} from 'components/SlideOver/slideOverUnsavedChanges';
import UseContext from 'hooks/useContext';
import InvoiceImportToast from './InvoiceImportToast';
import { INVOICE_IMPORT_STARTED } from './invoiceImportText';
import useInvoiceImportRun from './useInvoiceImportRun';

const STORAGE_KEY = 'invoiceImportRun';
const PERIOD_PATTERN = /^\d{4}-\d{2}$/;
const FINISHED_STATUSES = ['done', 'failed'];
const INVOICES_PATH = '/billing/invoices';

/** The search param of the invoices tab that opens the import journal on a tab (`toFix`). */
export const JOURNAL_PARAM = 'journal';

// Storage may be refused (private mode, quota): the run is then simply not restored.
const readStoredRun = () => {
  try {
    // Without a session the run belongs to a former login.
    if (!localStorage.getItem('xsrfToken')) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    if (typeof stored?.runId !== 'string' || !stored.runId) return null;
    return {
      runId: stored.runId,
      period: typeof stored.period === 'string' ? stored.period : null,
    };
  } catch {
    return null;
  }
};

const storeRun = (tracked) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tracked));
  } catch {
    // Not restored after a reload, nothing else lost.
  }
};

/** Forgets the followed run, so that a reload does not bring it back (logout). */
export const clearStoredImportRun = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored then.
  }
};

const InvoiceImportRunContext = createContext(null);

/**
 * The invoice import run followed by the admin and its toast, above the
 * routes: the toast stays on every page, and « Voir les erreurs » opens the
 * journal of the run's month from anywhere, closing the panel or modal open
 * on top. A run handed over by `modalDataReturn` (INVOICE_IMPORT_STARTED) is
 * followed from any page. A running run is kept in the session storage of the
 * tab, so that a reload follows it again; a finished one is forgotten.
 */
const InvoiceImportRunProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    modalDataReturn,
    slideOverShow,
    slideOverType,
    slideOverHasUnsavedChanges,
    setSlideOverShow,
    setSlideOverType,
    setDataCreateMission,
    setModalShow,
    removeAllParameters,
    language,
  } = UseContext();
  const [tracked, setTracked] = useState(readStoredRun);
  const [isToastHidden, setIsToastHidden] = useState(false);
  const runEndListeners = useRef(new Set());

  const endRun = useCallback((finishedRun) => {
    clearStoredImportRun();
    runEndListeners.current.forEach((listener) => listener(finishedRun));
  }, []);
  const forgetMissingRun = useCallback(() => {
    clearStoredImportRun();
    setTracked(null);
  }, []);

  const runId = tracked?.runId ?? null;
  const run = useInvoiceImportRun(runId, endRun, {
    onMissing: forgetMissingRun,
  });
  const isFinished = FINISHED_STATUSES.includes(run?.status);
  const isImportRunning = Boolean(runId) && !isFinished;

  const isFinishedRef = useRef(isFinished);
  isFinishedRef.current = isFinished;
  const runIdRef = useRef(runId);
  runIdRef.current = runId;

  const followImportRun = useCallback((nextRunId, period) => {
    if (!nextRunId) return;
    const next = { runId: String(nextRunId), period: period ?? null };
    setTracked(next);
    setIsToastHidden(false);
    // Followed again once over (no new read): it must not come back after a reload.
    if (!(next.runId === runIdRef.current && isFinishedRef.current)) {
      storeRun(next);
    }
  }, []);

  // The import modal and the invoice detail hand their run over here: the
  // detail may be opened from a company panel, far from the billing page.
  useEffect(() => {
    if (modalDataReturn?.type !== INVOICE_IMPORT_STARTED) return;
    followImportRun(modalDataReturn.runId, modalDataReturn.period);
  }, [modalDataReturn, followImportRun]);

  const subscribeToRunEnd = useCallback((listener) => {
    runEndListeners.current.add(listener);
    return () => runEndListeners.current.delete(listener);
  }, []);

  const closeToast = () => {
    setIsToastHidden(true);
    clearStoredImportRun();
  };

  const period = run?.period ?? tracked?.period;
  const showErrors = PERIOD_PATTERN.test(period ?? '')
    ? () => {
        // The journal shares the z-index of the panels and modals, which come
        // later in the DOM: left open, they would cover it.
        if (
          slideOverShow &&
          !confirmSlideOverClose(
            slideOverType,
            slideOverHasUnsavedChanges,
            language,
          )
        ) {
          return;
        }
        setModalShow(false);
        if (slideOverShow) {
          if (slideOverType === 'add_mission') {
            setDataCreateMission(INITIAL_CREATE_MISSION_DATA);
          }
          setSlideOverShow(false);
          setSlideOverType('');
          // Before navigating: it replaces the address with the current page's,
          // which would undo the navigation.
          removeAllParameters();
        }
        const [year, month] = period.split('-');
        // The page drops the param with a replace: pushed from the invoices
        // themselves, the history would hold the same entry twice.
        navigate(
          `${INVOICES_PATH}?y=${year}&m=${month}&${JOURNAL_PARAM}=toFix`,
          {
            replace: location.pathname === INVOICES_PATH,
          },
        );
      }
    : undefined;

  const value = useMemo(
    () => ({ run, runId, isImportRunning, followImportRun, subscribeToRunEnd }),
    [run, runId, isImportRunning, followImportRun, subscribeToRunEnd],
  );

  return (
    <InvoiceImportRunContext.Provider value={value}>
      {children}
      {runId && !isToastHidden && (
        <InvoiceImportToast
          run={run}
          onClose={closeToast}
          onShowErrors={showErrors}
        />
      )}
    </InvoiceImportRunContext.Provider>
  );
};

/**
 * The import run followed above the routes: `followImportRun(runId, period)`,
 * the polled `run`, `isImportRunning`. `onRunEnd(run)` is called once when a
 * followed run ends, while the caller is mounted.
 */
export const useInvoiceImportRunTracking = (onRunEnd) => {
  const value = useContext(InvoiceImportRunContext);
  if (!value) {
    throw new Error(
      'useInvoiceImportRunTracking must be used inside InvoiceImportRunProvider',
    );
  }
  const onRunEndRef = useRef(onRunEnd);
  onRunEndRef.current = onRunEnd;
  const { subscribeToRunEnd } = value;
  useEffect(
    () =>
      subscribeToRunEnd((finishedRun) => onRunEndRef.current?.(finishedRun)),
    [subscribeToRunEnd],
  );
  return value;
};

export default InvoiceImportRunProvider;
